if (!customElements.get('fidlock-variant-grid')) {
  class FidlockVariantGrid extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.variantPicker = this.closest('variant-picker');
      this.styleOptionName = this.dataset.styleOption;
      this.sizeOptionName = this.dataset.sizeOption;

      this.variantButtons = Array.from(
        this.querySelectorAll('[data-fidlock-variant]')
      );

      this.selectedText = this.querySelector(
        '[data-fidlock-selected-text]'
      );

      if (
        !this.variantPicker ||
        !this.styleOptionName ||
        !this.sizeOptionName
      ) {
        return;
      }

      this.variantData = this.getVariantData();

      this.normaliseVisibleLabels();
      this.addCustomStyles();
      this.applyCardPricing();

      this.handleClick = this.handleClick.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);

      this.addEventListener('click', this.handleClick);

      this.variantPicker.addEventListener(
        'on:variant:change',
        this.handleVariantChange
      );

      const initiallySelectedButton = this.variantButtons.find(
        (button) => button.getAttribute('aria-pressed') === 'true'
      );

      const startingButton =
        initiallySelectedButton || this.variantButtons[0];

      if (startingButton) {
        this.updateSelectedState(
          startingButton.dataset.variantId
        );

        window.requestAnimationFrame(() => {
          this.selectVariant(startingButton);
        });
      }
    }

    disconnectedCallback() {
      if (this.handleClick) {
        this.removeEventListener(
          'click',
          this.handleClick
        );
      }

      if (
        this.variantPicker &&
        this.handleVariantChange
      ) {
        this.variantPicker.removeEventListener(
          'on:variant:change',
          this.handleVariantChange
        );
      }

      delete this.dataset.initialized;
    }

    getVariantData() {
      const dataElement = this.variantPicker.querySelector(
        'script[type="application/json"]'
      );

      if (!dataElement) return null;

      try {
        return JSON.parse(dataElement.textContent);
      } catch (error) {
        console.warn(
          'The Fidlock variant picker could not read the Shopify variant data.',
          error
        );

        return null;
      }
    }

    stripHtml(value) {
      const temporaryElement =
        document.createElement('div');

      temporaryElement.innerHTML = value || '';

      return (
        temporaryElement.textContent ||
        temporaryElement.innerText ||
        ''
      ).trim();
    }

    normaliseVisibleLabels() {
      const capacityLabels = this.querySelectorAll(
        '.fidlock-picker__capacity'
      );

      capacityLabels.forEach((label) => {
        label.textContent = label.textContent
          .trim()
          .replace(/ML\b/g, 'ml');
      });

      this.variantButtons.forEach((button) => {
        const nameElement = button.querySelector(
          '.fidlock-picker__name'
        );

        if (!nameElement) return;

        const fullStyleName =
          button.dataset.styleValue || '';

        const shortStyleName = fullStyleName
          .split(' / ')[0]
          .trim();

        nameElement.textContent =
          shortStyleName || fullStyleName;
      });
    }

    addCustomStyles() {
      const existingStyles =
        document.getElementById(
          'fidlock-variant-custom-styles'
        );

      if (existingStyles) {
        existingStyles.remove();
      }

      const styleElement =
        document.createElement('style');

      styleElement.id =
        'fidlock-variant-custom-styles';

      styleElement.textContent = `
        .fidlock-picker__title {
          margin-bottom: 1rem !important;
          font-size: 1.4rem !important;
        }

        .fidlock-picker__group + .fidlock-picker__group {
          margin-top: 1.25rem !important;
        }

        .fidlock-picker__legend {
          gap: 0.5rem !important;
          margin-bottom: 0.55rem !important;
        }

        .fidlock-picker__capacity {
          font-size: 1.22rem !important;
          text-transform: none !important;
        }

        .fidlock-picker__descriptor {
          font-size: 1rem !important;
        }

        .fidlock-picker__selection {
          display: none !important;
        }

        .fidlock-picker__media {
          background: #ffffff !important;
        }

        .fidlock-picker__media img {
          width: 100% !important;
          height: 100% !important;
          object-fit: contain !important;
          object-position: center !important;
          transform: none !important;
        }

        .fidlock-picker__sale-badge {
          position: absolute;
          z-index: 3;
          top: 0.45rem;
          left: 0.45rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.3rem 0.42rem;
          color: #000000;
          font-size: 0.78rem;
          font-weight: 800;
          line-height: 1;
          white-space: nowrap;
          background: #00bfd3;
          border-radius: 4px;
          pointer-events: none;
        }

        .fidlock-picker__price {
          display: flex !important;
          flex-wrap: wrap;
          align-items: baseline;
          gap: 0.3rem;
          margin-top: auto !important;
        }

        .fidlock-picker__price-current {
          display: inline-block;
          color: rgba(0, 0, 0, 0.65);
          font-size: 1rem;
          font-weight: 400;
          line-height: 1.2;
          white-space: nowrap;
        }

        .fidlock-picker__price--sale
        .fidlock-picker__price-current {
          color: #e52b36;
          font-weight: 800;
        }

        .fidlock-picker__price-was {
          display: inline-block;
          color: rgba(0, 0, 0, 0.5);
          font-size: 0.84rem;
          font-weight: 400;
          line-height: 1.2;
          text-decoration: line-through;
          text-decoration-thickness: 1px;
          white-space: nowrap;
        }

        @media (min-width: 750px) {
          .fidlock-picker {
            margin-top: 1.8rem !important;
          }

          .fidlock-picker__cards {
            grid-template-columns:
              repeat(2, minmax(0, 1fr)) !important;
            gap: 0.6rem !important;
          }

          .fidlock-picker__card {
            display: grid !important;
            grid-template-columns:
              4.25rem minmax(0, 1fr);
            grid-template-rows:
              auto auto;
            column-gap: 0.6rem;
            align-items: center;
            min-height: 5.7rem;
            padding: 0.5rem !important;
          }

          .fidlock-picker__media {
            grid-column: 1;
            grid-row: 1 / span 2;
            width: 4.25rem !important;
            height: 4.8rem !important;
            aspect-ratio: auto !important;
            margin: 0 !important;
            padding: 0.1rem;
          }

          .fidlock-picker__name {
            grid-column: 2;
            grid-row: 1;
            display: -webkit-box !important;
            min-height: 0 !important;
            margin: 0 !important;
            overflow: hidden;
            font-size: 0.98rem !important;
            font-weight: 700;
            line-height: 1.12 !important;
            white-space: normal;
            overflow-wrap: normal !important;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
          }

          .fidlock-picker__price {
            grid-column: 2;
            grid-row: 2;
            margin-top: 0.15rem !important;
            padding-top: 0 !important;
          }

          .fidlock-picker__check {
            top: 0.4rem !important;
            right: 0.4rem !important;
            width: 1.55rem !important;
            height: 1.55rem !important;
            font-size: 0.95rem !important;
          }
        }

        @media (max-width: 749px) {
          .fidlock-picker {
            margin-top: 1.4rem !important;
          }

          .fidlock-picker__title {
            margin-bottom: 0.9rem !important;
            font-size: 1.28rem !important;
          }

          .fidlock-picker__group + .fidlock-picker__group {
            margin-top: 1.08rem !important;
          }

          .fidlock-picker__legend {
            margin-bottom: 0.5rem !important;
          }

          .fidlock-picker__capacity {
            font-size: 1.12rem !important;
          }

          .fidlock-picker__descriptor {
            font-size: 0.95rem !important;
          }

          .fidlock-picker__cards {
            display: grid !important;
            grid-template-columns:
              repeat(2, minmax(0, 1fr)) !important;
            grid-auto-columns: initial !important;
            grid-auto-flow: row !important;
            gap: 0.6rem !important;
            padding: 0 !important;
            overflow: visible !important;
            scroll-snap-type: none !important;
          }

          .fidlock-picker__card {
            display: grid !important;
            grid-template-columns:
              4.05rem minmax(0, 1fr);
            grid-template-rows:
              auto auto;
            column-gap: 0.45rem;
            align-items: center;
            min-height: 5.75rem;
            padding: 0.35rem !important;
            scroll-snap-align: none !important;
          }

          .fidlock-picker__media {
            grid-column: 1;
            grid-row: 1 / span 2;
            width: 4.05rem !important;
            height: 5.05rem !important;
            aspect-ratio: auto !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .fidlock-picker__name {
            grid-column: 2;
            grid-row: 1;
            display: -webkit-box !important;
            min-height: 0 !important;
            margin: 0 !important;
            overflow: hidden;
            font-size: 0.95rem !important;
            font-weight: 700;
            line-height: 1.1 !important;
            white-space: normal;
            overflow-wrap: normal !important;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
          }

          .fidlock-picker__price {
            grid-column: 2;
            grid-row: 2;
            margin-top: 0.2rem !important;
            padding-top: 0 !important;
            gap: 0.25rem;
          }

          .fidlock-picker__price-current {
            font-size: 0.95rem;
          }

          .fidlock-picker__price-was {
            font-size: 0.77rem;
          }

          .fidlock-picker__check {
            top: 0.35rem !important;
            right: 0.35rem !important;
            width: 1.48rem !important;
            height: 1.48rem !important;
            font-size: 0.86rem !important;
          }

          .fidlock-picker__sale-badge {
            top: 0.35rem;
            left: 0.35rem;
            padding: 0.26rem 0.35rem;
            font-size: 0.7rem;
          }
        }
      `;

      document.head.appendChild(styleElement);
    }

    applyCardPricing() {
      if (
        !this.variantData ||
        !Array.isArray(
          this.variantData.variants
        )
      ) {
        return;
      }

      this.variantButtons.forEach((button) => {
        const variantId = String(
          button.dataset.variantId
        );

        const variant =
          this.variantData.variants.find(
            (item) =>
              String(item.id) === variantId
          );

        const formatted =
          this.variantData.formatted &&
          this.variantData.formatted[variantId]
            ? this.variantData.formatted[variantId]
            : null;

        const priceElement =
          button.querySelector(
            '.fidlock-picker__price'
          );

        if (!variant || !priceElement) {
          return;
        }

        const currentPriceHtml =
          formatted && formatted.price
            ? formatted.price
            : priceElement.innerHTML.trim();

        const compareAtPriceHtml =
          formatted &&
          formatted.compareAtPrice
            ? formatted.compareAtPrice
            : '';

        const numericPrice =
          Number(variant.price);

        const numericCompareAtPrice =
          Number(variant.compare_at_price);

        const isOnSale =
          Number.isFinite(
            numericCompareAtPrice
          ) &&
          numericCompareAtPrice >
            numericPrice &&
          numericCompareAtPrice > 0;

        const existingBadge =
          button.querySelector(
            '.fidlock-picker__sale-badge'
          );

        if (existingBadge) {
          existingBadge.remove();
        }

        priceElement.innerHTML = '';

        priceElement.classList.remove(
          'fidlock-picker__price--sale'
        );

        const currentPriceElement =
          document.createElement('span');

        currentPriceElement.className =
          'fidlock-picker__price-current';

        currentPriceElement.innerHTML =
          currentPriceHtml;

        priceElement.appendChild(
          currentPriceElement
        );

        if (!button.dataset.baseAriaLabel) {
          button.dataset.baseAriaLabel =
            button.getAttribute(
              'aria-label'
            ) || '';
        }

        if (!isOnSale) {
          button.classList.remove(
            'fidlock-picker__card--sale'
          );

          button.setAttribute(
            'aria-label',
            button.dataset.baseAriaLabel
          );

          return;
        }

        const discountPercentage =
          Math.round(
            (
              (
                numericCompareAtPrice -
                numericPrice
              ) /
              numericCompareAtPrice
            ) * 100
          );

        button.classList.add(
          'fidlock-picker__card--sale'
        );

        priceElement.classList.add(
          'fidlock-picker__price--sale'
        );

        const badge =
          document.createElement('span');

        badge.className =
          'fidlock-picker__sale-badge';

        badge.textContent =
          `${discountPercentage}% off`;

        badge.setAttribute(
          'aria-hidden',
          'true'
        );

        button.appendChild(badge);

        if (compareAtPriceHtml) {
          const previousPriceElement =
            document.createElement('span');

          previousPriceElement.className =
            'fidlock-picker__price-was';

          previousPriceElement.innerHTML =
            compareAtPriceHtml;

          priceElement.appendChild(
            previousPriceElement
          );
        }

        const currentPriceText =
          this.stripHtml(
            currentPriceHtml
          );

        const compareAtPriceText =
          this.stripHtml(
            compareAtPriceHtml
          );

        const saleDescription =
          compareAtPriceText
            ? `${discountPercentage}% off, now ${currentPriceText}, was ${compareAtPriceText}`
            : `${discountPercentage}% off, now ${currentPriceText}`;

        button.setAttribute(
          'aria-label',
          `${button.dataset.baseAriaLabel}, ${saleDescription}`
        );
      });
    }

    handleClick(event) {
      const button = event.target.closest(
        '[data-fidlock-variant]'
      );

      if (
        !button ||
        !this.contains(button)
      ) {
        return;
      }

      this.selectVariant(button);
    }

    selectVariant(button) {
      const styleValue =
        button.dataset.styleValue;

      const sizeValue =
        button.dataset.sizeValue;

      const styleInput =
        this.findNativeOptionInput(
          this.styleOptionName,
          styleValue
        );

      const sizeInput =
        this.findNativeOptionInput(
          this.sizeOptionName,
          sizeValue
        );

      if (
        !styleInput ||
        !sizeInput
      ) {
        console.warn(
          'The Fidlock variant picker could not find the matching native Shopify options.'
        );

        return;
      }

      styleInput.checked = true;
      sizeInput.checked = true;

      styleInput.dispatchEvent(
        new Event('change', {
          bubbles: true
        })
      );
    }

    findNativeOptionInput(
      optionName,
      optionValue
    ) {
      const selector = Array.from(
        this.variantPicker.querySelectorAll(
          '.option-selector[data-selector-type="listed"]'
        )
      ).find(
        (element) =>
          element.dataset.option ===
          optionName
      );

      if (!selector) return null;

      return (
        Array.from(
          selector.querySelectorAll(
            'input.js-option'
          )
        ).find(
          (input) =>
            input.value === optionValue
        ) || null
      );
    }

    handleVariantChange(event) {
      const variant =
        event.detail &&
        event.detail.variant;

      if (!variant) return;

      this.updateSelectedState(
        String(variant.id)
      );
    }

    updateSelectedState(variantId) {
      let selectedButton = null;

      this.variantButtons.forEach(
        (button) => {
          const isSelected =
            button.dataset.variantId ===
            String(variantId);

          button.setAttribute(
            'aria-pressed',
            isSelected
              ? 'true'
              : 'false'
          );

          if (isSelected) {
            selectedButton = button;
          }
        }
      );

      if (
        !selectedButton ||
        !this.selectedText
      ) {
        return;
      }

      this.selectedText.textContent =
        `${selectedButton.dataset.styleValue} / ${selectedButton.dataset.sizeValue}`;
    }
  }

  customElements.define(
    'fidlock-variant-grid',
    FidlockVariantGrid
  );
}